import {
  rdbV2UsersPredictionsDetail,
  type RdbV2UsersPredictionsDetailParams,
  type RpmPickList,
} from '@on3/api';
import type { ErrorData } from '@on3/ui-lib/api/schema/custom-contracts';
import { externalApi, useSite } from '@on3/ui-lib/index';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { type AxiosError } from 'axios';
import { PredictionCenterFilters } from 'components/Db/PredictionCenter/PredictionCenterFilters';
import { PredictionCenterPercentages } from 'components/Db/PredictionCenter/PredictionCenterPercentages';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';
import type { IPredictionPageProps } from 'pages/user/[slug]/predictions';
import { useCallback, useState } from 'react';

import { PredictionHeader } from './PredictionHeader';
import { PredictionItem } from './PredictionItem';
import styles from './PredictionItem.module.scss';

const arr: [] = [];

export const PredictionList = ({
  title,
  pickData,
  filters,
}: IPredictionPageProps) => {
  const { list, pagination, relatedModel } = pickData ?? {};
  const { query, replace } = useRouter();
  const {
    sport,
    year = 'all',
    page = 1,
  } = query as {
    sport?: string;
    year?: string;
    page?: string;
  };

  const { currentSite, sportsData } = useSite();
  const name =
    currentSite?.organization?.knownAs ?? currentSite?.organization?.name;
  const dailyTitle = `${year} ${
    (!currentSite?.isNational && name) || ''
  } Picks`;
  const allTimeTitle = `All-Time ${
    (!currentSite?.isNational && name) || ''
  } Picks`;
  const defaultPage = parseInt(`${page}`, 10);

  const [disable, setDisable] = useState(false);
  const [pageNumber, setPageNumber] = useState(defaultPage || 1);
  const [predictionList, setPredictionList] = useState<RpmPickList[]>(
    list ?? arr,
  );

  const { sports, years } = filters;
  const availableYears = years.filter((yr) => yr >= 2022);
  const userKey = relatedModel?.userRecord?.key;
  const {
    all: allTimePredictionAccuracy,
    year: currentYearsPredictionAccuracy,
  } = relatedModel?.predictionAccuracy ?? {};

  const hasCurrentYearsPredictionAccuracy =
    !!currentYearsPredictionAccuracy?.total;

  const hasAllTimePredictionAccuracy = !!allTimePredictionAccuracy?.total;

  const showLoadMoreButton =
    pagination?.count &&
    pagination?.count > 50 &&
    (pagination?.pageCount ?? 0) > pageNumber &&
    predictionList?.length < pagination?.count;

  const handleClick: React.MouseEventHandler = useCallback(async () => {
    setDisable(true);
    const newPageNumber = pageNumber + 1;

    const sportKey = sportsData?.find(
      (sp) => sp?.name && slugify(sp?.name) === sport,
    )?.key;

    const params: RdbV2UsersPredictionsDetailParams = {
      page: newPageNumber,
      sportKey,
      year: year === 'all' ? undefined : +year,
      userKey: userKey || 0,
    };

    try {
      const response = await rdbV2UsersPredictionsDetail(externalApi, params);

      if (!response?.list) {
        return;
      }

      const currentPage = response.pagination?.currentPage;

      if (currentPage) {
        replace(
          {
            query: { ...query, page: currentPage },
          },
          undefined,
          { shallow: true },
        );
        setPredictionList([...predictionList, ...response.list]);
        setPageNumber(currentPage);
      }
    } catch (e: unknown) {
      const { response, message }: AxiosError<ErrorData> =
        e as AxiosError<ErrorData>;

      console.error(
        'User Prediction Error: ',
        response?.data?.message || message,
      );
    } finally {
      setDisable(false);
    }
  }, [
    pageNumber,
    predictionList,
    query,
    replace,
    sport,
    sportsData,
    userKey,
    year,
  ]);

  return (
    <div className="">
      <header>
        <Text className={styles.pageTitle} variant="h1">
          {title}
        </Text>
      </header>
      <PredictionCenterFilters sports={sports} years={availableYears} />
      <div className={styles.percentageContainer}>
        {hasCurrentYearsPredictionAccuracy && year !== 'all' && (
          <PredictionCenterPercentages
            accuracy={currentYearsPredictionAccuracy}
            percentageClassName={styles.currentYearsPrediction}
            percentageTitle={dailyTitle}
          />
        )}
        {hasAllTimePredictionAccuracy && (
          <PredictionCenterPercentages
            accuracy={allTimePredictionAccuracy}
            percentageTitle={allTimeTitle}
          />
        )}
      </div>

      <PredictionHeader />
      {predictionList?.map((item) => {
        return (
          <PredictionItem
            key={`${item?.player?.key}-${sport}-${year}`}
            {...item}
          />
        );
      })}
      {!predictionList.length && (
        <Text>No User Predictions data for this selection.</Text>
      )}
      {!!showLoadMoreButton && (
        <PrimaryButton
          className={styles.btnLoadMore}
          disabled={disable}
          fullWidth={false}
          onClick={handleClick}
          variant="text"
        >
          Load More
        </PrimaryButton>
      )}
    </div>
  );
};
